import { ZodType, z } from "zod";

const disabledDisclaimer =
  "Disabled: To purchase this option, please contact our sales department by clicking the 'Create Support Ticket' button on the Help Center page.";

export const WebSdkSettings = z
  .object({
    conf: z
      .object({
        fullscreenButton: z
          .boolean()
          .default(true)
          .describe(
            [
              "Full-Screen Button|",
              "Override Enable/Disable Enable/Disable the full-screen button.",
              "Info: Please be aware that iOS (iPhone devices) does not support full-screen mode, and as a result, the button will not be displayed on these devices.",
            ].join("\n")
          )
          .optional(),
        stopButton: z
          .boolean()
          .default(true)
          .describe(
            [
              "Stop Button|",
              "Override Enable/Disable the stop overlay-button.",
              "Info: Manually stops the connection.",
            ].join("\n")
          )
          .optional(),
        // Default: false, for cleaner UI.
        audioButton: z
          .boolean()
          .default(false)
          .describe(
            [
              "Audio Button|",
              "Override Enable/Disable the audio overlay-button.",
              "Info: Displays the audio button if your Unreal Engine application includes audio.",
            ].join("\n")
          )
          .optional(),
        // Even though it's defaulted to false, it can still be enabled while being false by using the "i" query parameter: https://some-url/?i
        infoButton: z
          .boolean()
          .default(false)
          .describe(["Info Button|", "Override Enable/Disable the info overlay-button."].join("\n"))
          .optional(),
        micButton: z
          .boolean()
          .default(false)
          .describe(
            [
              "Mic Button|",
              "Override Enable/Disable the microphone overlay-button.",
              "Info: Enables users to interact with the Unreal Engine application using a microphone, which must be manually enabled by the user.",
            ].join("\n")
          )
          .optional(),
        settingsButton: z
          .boolean()
          .default(false)
          .describe(["Settings Button|", "Override Enable/Disable the settings overlay-button."].join("\n"))
          .optional(),
        connectionStrengthIcon: z
          .boolean()
          .default(false)
          .describe(
            ["Connection Strength|", "Override Enable/Disable the connection strength overlay-icon."].join("\n")
          )
          .optional(),

        // errorHandler: zat(z.boolean()).optional(),
        // loveLetterHandler: zat(z.boolean()).optional(),
        // loveLetterLogging: zat(z.boolean()).optional(),
        // Enabled by default.
        // connectionIdentifierLoggingDisabled: zat(z.boolean()).optional(),
        // queueHandler: zat(z.boolean()).optional(),
        // sessionIdHandler: zat(z.boolean()).optional(),
      })
      .optional(),
    init: z
      .object({
        // Input
        KeyboardInput: z
          .boolean()
          .default(true)
          .describe(
            [
              "Keyboard Input|",
              "Override 'KeyboardInput' property of the WebSDK integration.",
              "Info: Allows keyboard input events.",
            ].join("\n")
          )
          .optional(),
        MouseInput: z
          .boolean()
          .default(true)
          .describe(
            [
              "Mouse Input|",
              "Override 'MouseInput' property of the WebSDK integration.",
              "Info: Allows mouse input events.",
            ].join("\n")
          )
          .optional(),
        GamepadInput: z
          .boolean()
          .describe(
            [
              "Game pad Input|",
              "Override 'GamepadInput' property of the WebSDK integration.",
              "Info: Allows input events from gamepad controllers.",
            ].join("\n")
          )
          .optional(),
        TouchInput: z
          .boolean()
          .describe(
            [
              "Touch Input|",
              "Override 'TouchInput' property of the WebSDK integration.",
              "Info: Allows touch events on mobile devices and tablets.",
            ].join("\n")
          )
          .optional(),
        XRControllerInput: z
          .boolean()
          .describe(
            [
              "XR Controller Input|",
              "Override 'XRControllerInput' property of the WebSDK integration.",
              "Info: Allows input events from XR controllers for use with AR and VR setups.",
            ].join("\n")
          )
          .optional(),
        // UseMic:
        //   z.boolean().describe(["UseMic|", "Override use mic property of the WebSDK integration."].join("\n"))
        //  .optional(),

        FakeMouseWithTouches: z
          .boolean()
          .describe(
            [
              "Fake mouse with touches (Only for Admins)|",
              "Override 'FakeMouseWithTouches' property of the WebSDK integration. When enabled this setting will convert single-finger touch input to mouse input.",
              "Info: When this option is enabled, and the user is viewing the stream on a device with a touch screen such as a smartphone or tablet, this setting causes single-finger touch events to be interpreted by the Unreal Engine application as mouse clicks and drag events. Enabling this setting can provide users on mobile devices with the ability to partially control your Unreal Engine application, even when the application's input controller does not specifically handle touch input events.",
            ].join("\n")
          )
          .optional(),
        ForceMonoAudio: z
          .boolean()
          .describe(["Force mono audio|", "Override 'ForceMonoAudio' of the WebSDK integration."].join("\n"))
          .optional(),
        HoveringMouse: z
          .boolean()
          .describe(["Hovering mouse|", "Override 'HoveringMouse' property of the WebSDK integration."].join("\n"))
          .optional(),
        // MatchViewportRes: zat(
        //   z
        //     .boolean()
        //     .describe(["MatchViewportRes|", "Override match viewport res property of the WebSDK integration."].join("\n"))
        // ).optional(),
        // SuppressBrowserKeys: zat(
        //   z
        //     .boolean()
        //     .describe(
        //       ["SuppressBrowserKeys|", "Override suppress browser keys property of the WebSDK integration."].join("\n")
        //     )
        // ).optional(),

        // These make no sense, since the data flows only with the streamInfo
        // AutoConnect: zat(
        //   z.boolean().default(false).describe(["AutoConnect|", "To be described."].join("\n"))
        // ).optional(),
        // StartVideoMuted: zat(
        //   z.boolean().default(true).describe(["StartVideoMuted|", "To be described."].join("\n"))
        // ).optional(),
        // AutoPlayVideo: zat(
        //   z.boolean().default(true).describe(["AutoPlayVideo|", "To be described."].join("\n"))
        // ).optional(),

        // No effect:
        // ForceTURN: zat(z.boolean().describe(["ForceTURN|", "To be described."].join("\n"))).optional(),
        // AFKTimeout: zat(z.number().describe(["AFKTimeout|", "To be described."].join("\n"))).optional(),
        // MaxQP: zat(z.number().int().min().max().describe(["Todo Title|","To be described."].join("\n"))).optional(),
        // MinQP: zat(z.number().int().min().max().describe(["Todo Title|","To be described."].join("\n"))).optional(),
        // OfferToReceive: zat(z.boolean().describe(["Todo Title|","To be described."].join("\n"))).optional(),
        // PreferredCodec: zat(z.literal().describe(["Todo Title|","To be described."].join("\n"))).optional(),
        // preferSFU: zat(z.boolean().describe(["Todo Title|","To be described."].join("\n"))).optional(),
        // StreamerId: zat(z.boolean().describe(["Todo Title|","To be described."].join("\n"))).optional(),
        // TimeoutIfIdle: zat(z.boolean().describe(["Todo Title|","To be described."].join("\n"))).optional(),

        // Don't know whether we want to have that or not.
        // ControlsQuality: zat(z.boolean().describe(["ControlsQuality|", "To be described."].join("\n"))).optional(),
        // WebRTCFPS: zat(z.number().describe(["WebRTCFPS|", "To be described."].join("\n"))).optional(),
        // WebRTCMaxBitrate: zat(z.number().describe(["WebRTCMaxBitrate|", "To be described."].join("\n"))).optional(),
        // WebRTCMinBitrate: zat(z.number().describe(["WebRTCMinBitrate|", "To be described."].join("\n"))).optional(),
      })
      .optional(),
  })
  .strict();
